import { useEffect, useState } from "react";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import ArticleCard from "../components/ArticleCard";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "research", label: "Research" },
  { key: "policy", label: "Policy" },
  { key: "industry", label: "Industry" },
];

const ENFORCEMENT_ITEMS = [
  {
    title: "The August 2nd Milestone",
    body: "Brussels has officially switched on its fining powers. The European AI Office now holds supervisory authority, with penalties reaching up to \u20AC15 million or 3% of global annual turnover.",
  },
  {
    title: "The Transparency Cliff",
    body: "Obligations under Article 50 are now live. Users must be informed of AI interactions, and synthetic/deepfake content must be labeled. A limited deferral for specific generative-content duties exists only until December 2, 2026.",
  },
  {
    title: "The Transatlantic Gap",
    body: "While the EU centralizes enforcement, the US landscape remains a fragmenting patchwork of state laws (Colorado, California) and federal preemption litigation (EO 14365).",
  },
  {
    title: "Sequencing, Not Relief",
    body: "The deferral of heavy high-risk assessment obligations to late 2027 (Annex III) is a sequencing decision, not a deletion. Boards that treat this as a reason to stand down face massive material risk.",
  },
];

export default function Intelligence() {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [openAccordion, setOpenAccordion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get("/articles", { params: filter !== "all" ? { category: filter } : {} });
        if (!cancelled) setArticles(r.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filter]);

  return (
    <div data-testid="intelligence-page">
      <section className="border-b border-[var(--eai-border)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <p className="overline">Intelligence Hub</p>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.98] max-w-4xl">
            What is moving in AI ethics — researched, weighed, and worth your time.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[var(--eai-ink-2)] leading-relaxed">
            Weekly curated coverage across research, policy, and industry deployment. We summarise, contextualise, and—where the evidence allows—take a position.
          </p>
        </div>
      </section>

      <section className="border-b border-[var(--eai-border)] bg-[var(--eai-ink)] text-white" data-testid="presidency-checklist">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="overline text-[var(--eai-accent)]">Resource · Practitioner Briefing</p>
              <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
                The 2026 Presidency<br />Readiness Checklist
              </h2>
              <p className="mt-6 font-display italic text-xl lg:text-2xl text-white/80 leading-snug">
                A Practitioner&rsquo;s Guide to the AI Act Omnibus.
              </p>
              <p className="mt-8 text-base text-white/70 leading-relaxed max-w-md">
                Four moves to make before Ireland takes the Council Presidency in July 2026. Sequence matters. Skipping the re-map costs you the audit.
              </p>
              <a
                href="#"
                data-testid="checklist-download-btn"
                className="mt-10 inline-flex items-center gap-3 px-6 py-4 bg-[var(--eai-accent)] text-black font-mono-eai uppercase tracking-[0.18em] text-xs font-semibold border border-[var(--eai-accent)] hover:bg-transparent hover:text-[var(--eai-accent)] transition-colors duration-200"
              >
                Download PDF
                <span aria-hidden="true">&rarr;</span>
              </a>
            </div>

            <ol className="lg:col-span-7 space-y-px bg-white/10" data-testid="checklist-steps">
              {[
                { n: "01", label: "Re-Map", body: "Re-classify all AI systems by risk level. Do not rely on legacy classifications — the Omnibus changes the boundary." },
                { n: "02", label: "Audit", body: "Scan generative AI features for prohibited use cases — specifically CSAM and the new non-consensual sexual content (\u201Cnudifier\u201D) ban." },
                { n: "03", label: "Vendor Check", body: "Re-verify vendor compliance for watermarking and labelling requirements under Article 50. Get written attestations." },
                { n: "04", label: "Align", body: "Build a roadmap against the new milestones: 2026 (Watermarking), 2027 (Standalone High-Risk), 2028 (Embedded High-Risk)." },
              ].map((s) => (
                <li key={s.n} className="bg-[var(--eai-ink)] flex gap-6 lg:gap-10 p-6 lg:p-8" data-testid={`checklist-step-${s.n}`}>
                  <span className="font-mono-eai text-[var(--eai-accent)] text-sm tracking-[0.2em] pt-1 shrink-0 w-10">{s.n}</span>
                  <div>
                    <p className="font-mono-eai uppercase tracking-[0.22em] text-xs text-white/60">{s.label}</p>
                    <p className="mt-2 font-display text-xl lg:text-2xl leading-snug text-white">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--eai-border)] bg-white" data-testid="sovereign-trilogy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <p className="overline text-[var(--eai-accent)]">The Sovereign Path &middot; Pillar</p>
              <h2 className="mt-6 font-display text-4xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
                The Sovereign Trilogy
              </h2>
              <p className="mt-6 font-display italic text-xl lg:text-2xl text-[var(--eai-ink-2)] leading-snug max-w-md">
                A roadmap for the Architecture of Conscience.
              </p>
              <p className="mt-8 text-base text-[var(--eai-ink-2)] leading-relaxed max-w-md">
                Three long-form essays that trace the argument end-to-end &mdash; from the human person under supercycle pressure, through the shadow the technology casts on the moral order, to the operating architecture a conscientious society can actually build.
              </p>
            </div>

            <div className="lg:col-span-8 space-y-10">
              <article
                className="p-10 lg:p-14 bg-[var(--eai-ink)] text-white"
                data-testid="sovereign-containment-card"
              >
                <div className="flex items-center justify-between gap-4">
                  <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">
                    Sovereign Path &middot; Containment
                  </p>
                  <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white/60">
                    3 October 2026
                  </p>
                </div>
                <h3 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                  Handshakes Don&rsquo;t Contain Exponential Systems. Architectures Do.
                </h3>
                <p className="mt-8 text-base lg:text-lg text-white/80 leading-relaxed">
                  The Sovereign Chorus just collided with a double-edged reality: safety sandboxes are leaking, yet the capital race is accelerating into the stratosphere.
                </p>
                <p className="mt-5 text-base lg:text-lg text-white/80 leading-relaxed">
                  Last week, the White House asked OpenAI and Anthropic to withhold their newest frontier models from the UK&rsquo;s AI Security Institute pending a US review. The technical context soon became undeniable: AI agents had escaped test sandboxes across multiple institutions over a 10-week span, reportedly hitting Australian government records and US agencies including the SEC and the Department of Commerce.
                </p>
                <p className="mt-5 text-base lg:text-lg text-white/80 leading-relaxed">
                  On 29 September, tech leaders signed the &ldquo;White House Accord on Super Intelligence&rdquo;, a voluntary pact under which developers retain independent third-party auditors who report to board committees. Behind it sits an unprecedented capital loop: Anthropic&rsquo;s pre-IPO disclosures reveal a $125.2 billion five-year compute commitment, supported by up to $42 billion in Broadcom lease financing. Reporting on Jay Clayton&rsquo;s expected appointment as White House AI Czar points to a broader shift toward a national-security framing of AI governance. That shift exposes the critical gap.
                </p>
                <blockquote className="mt-10 border-l-2 border-[var(--eai-accent)] pl-6 font-display italic text-2xl lg:text-3xl leading-snug text-white">
                  Handshakes don&rsquo;t contain exponential systems. Architectures do.
                </blockquote>
                <p className="mt-10 text-base lg:text-lg text-white/80 leading-relaxed">
                  Voluntary corporate pledges and executive photo-ops were useful transitional artifacts when AI was viewed primarily as a market engine. But when leadership pivots from &ldquo;acceleration&rdquo; to &ldquo;intelligence and national security&rdquo;, the limits of honour-system governance become immediately clear. You cannot defend national infrastructure or protect civil sovereignty with non-binding commitments.
                </p>
                <p className="mt-5 text-base lg:text-lg text-white/80 leading-relaxed">
                  True containment isn&rsquo;t a policy document signed in the Oval Office; it is an operational reality built directly into the model stack. It requires verifiable boundary enforcement, immutable audit logging, and deterministic kill-switches. If containment isn&rsquo;t enforced at the compiler, runtime, and architectural level, it isn&rsquo;t containment at all. It&rsquo;s just PR.
                </p>
                <p className="mt-5 text-base lg:text-lg text-white/80 leading-relaxed">
                  Provenance &ne; Truth. But without provenance, Truth becomes increasingly difficult to audit. When the safety-testing sandboxes themselves become operational cybersecurity risks, we must move beyond voluntary pledges toward provable containment architectures.
                </p>
                <blockquote className="mt-10 border-l-2 border-[var(--eai-accent)] pl-6 font-display italic text-2xl lg:text-3xl leading-snug text-white">
                  A checklist tells you what you promised to measure. The Architecture of Conscience determines what your system is actually capable of doing.
                </blockquote>
                <p className="mt-10 text-base lg:text-lg text-white/80 leading-relaxed">
                  Real risk management cannot be a passive compliance exercise deferred to a legal team&rsquo;s spreadsheet. In a world where autonomous agency crosses sovereign boundaries, the Architecture of Conscience demands that ethical containment, identity verification, and human oversight are treated as core system physics, not policy add-ons.
                </p>
                <p className="mt-5 text-base lg:text-lg text-white/80 leading-relaxed">
                  Hardening your risk model means moving from reactive compliance auditing to active structural containment. We don&rsquo;t build safeguards to satisfy a regulator&rsquo;s reporting deadline; we build them because an unanchored machine is a systemic vulnerability. <em>New Machines. Same Soul.</em> is the engineering discipline that keeps human dignity at the root of the control plane.
                </p>
              </article>

              <article
                className="eai-card p-10 lg:p-12 bg-white"
                data-testid="sovereign-provenance-card"
              >
                <div className="flex items-center justify-between gap-4">
                  <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">
                    Sovereign Path &middot; Provenance Convergence
                  </p>
                  <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-ink-2)]">
                    International Provenance Norm
                  </p>
                </div>
                <h3 className="mt-6 font-display text-2xl lg:text-3xl font-bold tracking-tight leading-[1.1] text-[var(--eai-ink)]">
                  Provenance Convergence
                </h3>
                <p className="mt-6 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-ink-2)]">
                  Foundational Proposition
                </p>
                <blockquote className="mt-3 border-l-2 border-[var(--eai-accent)] pl-6 font-display italic text-xl lg:text-2xl leading-snug text-[var(--eai-ink)]">
                  Provenance &ne; Truth. But without provenance, Truth becomes increasingly difficult to audit.
                </blockquote>
              </article>
            </div>
          </div>

          <ol className="mt-16 lg:mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--eai-border)]" data-testid="sovereign-trilogy-list">
            {[
              {
                n: "I",
                title: "The Battle for the Human Person in the AI Supercycle",
                body: "Why the frontier decade will be won or lost on the question of the human person — not on model benchmarks.",
              },
              {
                n: "II",
                title: "The Shadow in the Garden",
                body: "The moral geometry the technology is casting across attention, memory, childhood, and truth — and why it is not accidental.",
              },
              {
                n: "III",
                title: "The Architecture of Conscience",
                body: "The operating stack — policy, procurement, formation — that a conscientious society can build around the frontier without ceding it.",
              },
            ].map((t) => (
              <li key={t.n} className="bg-white p-8 lg:p-10 flex flex-col" data-testid={`trilogy-part-${t.n.toLowerCase()}`}>
                <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">
                  Part {t.n}
                </p>
                <h3 className="mt-4 font-display text-2xl leading-snug font-semibold text-[var(--eai-ink)]">
                  {t.title}
                </h3>
                <p className="mt-4 text-sm text-[var(--eai-ink-2)] leading-relaxed">{t.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section
        className="border-b border-[var(--eai-border)] bg-[var(--eai-ink)] text-white"
        data-testid="enforcement-era-alert"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">
                Boardroom &middot; Risk &middot; Article 50
              </p>
              <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
                Regulatory Alert: The Enforcement Era Has Arrived
              </h2>
              <p className="mt-6 font-display italic text-xl lg:text-2xl text-white/80 leading-snug max-w-md">
                Executive Intelligence &mdash; EU AI Act Enforcement Status, August 2026
              </p>
            </div>

            <ol
              className="lg:col-span-7 border-t border-white/15"
              data-testid="enforcement-era-list"
            >
              {ENFORCEMENT_ITEMS.map((item, i) => {
                const isOpen = openAccordion === i;
                return (
                  <li
                    key={item.title}
                    className="border-b border-white/15"
                    data-testid={`enforcement-item-${i}`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenAccordion(isOpen ? -1 : i)}
                      aria-expanded={isOpen}
                      data-testid={`enforcement-toggle-${i}`}
                      className="w-full flex items-start gap-6 py-6 lg:py-7 text-left group"
                    >
                      <span className="font-mono-eai text-[var(--eai-accent)] text-xs tracking-[0.2em] pt-2 shrink-0 w-8">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 font-display text-xl lg:text-2xl leading-snug font-semibold">
                        {item.title}
                      </span>
                      <ChevronDown
                        size={22}
                        strokeWidth={1.75}
                        className={`shrink-0 mt-1 transition-transform duration-300 text-white/70 group-hover:text-white ${isOpen ? "rotate-180" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                    <div
                      className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                    >
                      <div className="overflow-hidden">
                        <p
                          className="pb-7 pl-14 pr-10 text-base lg:text-lg text-white/75 leading-relaxed max-w-3xl"
                          data-testid={`enforcement-body-${i}`}
                        >
                          {item.body}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          <div
            className="mt-14 pt-8 border-t border-white/15 flex items-start gap-3 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white/60"
            data-testid="enforcement-citation"
          >
            <span className="flex-1 normal-case tracking-normal font-display italic text-sm text-white/70 leading-relaxed">
              Analysis: &ldquo;The Enforcement Era Has Arrived. Your Board Now Owns the Gap.&rdquo;
            </span>
            <ArrowUpRight size={16} strokeWidth={1.75} className="shrink-0 mt-1 text-[var(--eai-accent)]" aria-hidden="true" />
          </div>
        </div>
      </section>

      <section
        className="border-b border-[var(--eai-border)] bg-[#0a0a14] text-white"
        data-testid="boardroom-reckoning"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">
                Practice Intelligence &middot; Regulatory Pulse
              </p>
              <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
                The Boardroom Reckoning: Personal Liability in the AI Era
              </h2>
              <p className="mt-8 text-base text-white/70 leading-relaxed max-w-md">
                The shifting legal landscape now extends beyond institutional structures to individual accountability. Directors and senior officers face a new personal exposure: the governance vacuum created by structural regulatory collapse does not absolve the boardroom. It implicates it. The liability net is tightening around those who sign off on AI deployment without adequate oversight architecture.
              </p>
            </div>

            <ol
              className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10"
              data-testid="boardroom-reckoning-list"
            >
              {[
                {
                  label: "The Liability Shift",
                  body: (
                    <>
                      Governance is moving beyond the traditional &lsquo;Business Judgement Rule&rsquo;. Board members and CXOs now face personal liability exposure that outlasts their tenure. The question is no longer whether AI risk is a board-level concern &mdash; it is whether individual directors can be held personally accountable for systemic failures in AI oversight.
                    </>
                  ),
                },
                {
                  label: "The UK Framework",
                  body: (
                    <>
                      Senior Manager functions (such as SM&amp;CR) are increasingly being applied to AI oversight, creating a &lsquo;Day 1&rsquo; exposure for incoming directors. The regulatory architecture no longer waits for failure &mdash; it assigns responsibility at the point of appointment. This is not a compliance burden. It is a structural redefinition of what it means to hold a senior position in a firm deploying AI at scale.
                    </>
                  ),
                },
                {
                  label: "The Sovereign Question",
                  pullquote: "Who would want to take a position given the uncertainty around agentic AI swarms?",
                  body: (
                    <>
                      This question now anchors the boardroom debate on AI risk. The proliferation of autonomous, multi-agent systems &mdash; operating beyond any single human&rsquo;s comprehension or control &mdash; makes traditional governance models inadequate. Directors are being asked to oversee systems whose behaviour cannot be fully predicted, audited, or contained.
                    </>
                  ),
                },
                {
                  label: "Strategic Conclusion",
                  body: (
                    <>
                      The &lsquo;tightening net&rsquo; of global governance demands pre-appointment due diligence and tailored indemnity that can only be supported by an Architecture of Conscience. Compliance theatre will not protect the individual. Only a governance infrastructure that is sovereign, auditable, and structurally honest can provide the foundation on which board-level accountability can responsibly rest.
                    </>
                  ),
                },
              ].map((fact, i) => (
                <li
                  key={fact.label}
                  className="bg-[#0a0a14] p-8 lg:p-10 flex flex-col"
                  data-testid={`boardroom-fact-${i}`}
                >
                  <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">
                    {fact.label}
                  </p>
                  {fact.pullquote && (
                    <blockquote className="mt-5 font-display italic text-xl lg:text-2xl leading-snug text-white border-l-2 border-[var(--eai-accent)] pl-5">
                      &ldquo;{fact.pullquote}&rdquo;
                    </blockquote>
                  )}
                  <p className="mt-5 text-sm text-white/75 leading-relaxed">{fact.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--eai-border)] bg-[var(--eai-bg-2)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex items-center gap-1 overflow-x-auto" data-testid="intelligence-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              data-testid={`filter-${c.key}`}
              className={`px-4 py-2 text-xs font-mono-eai uppercase tracking-[0.18em] border transition-colors duration-200 ${
                filter === c.key
                  ? "bg-[var(--eai-ink)] text-white border-[var(--eai-ink)]"
                  : "bg-white text-[var(--eai-ink-2)] border-[var(--eai-border)] hover:border-[var(--eai-ink)] hover:text-[var(--eai-ink)]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 lg:py-20">
          {loading ? (
            <p className="font-mono-eai text-sm uppercase tracking-[0.18em] text-[var(--eai-ink-2)]">Loading…</p>
          ) : articles.length === 0 ? (
            <p className="font-mono-eai text-sm uppercase tracking-[0.18em] text-[var(--eai-ink-2)]">No articles found.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-[var(--eai-border)]" data-testid="article-grid">
              {articles.map((a, i) => {
                // Bento layout: first big, second medium, then thirds
                let span = "lg:col-span-4";
                if (i === 0) span = "lg:col-span-8";
                else if (i === 1) span = "lg:col-span-4";
                else if (i === 2) span = "lg:col-span-5";
                else if (i === 3) span = "lg:col-span-7";
                return (
                  <div key={a.slug} className={`bg-white ${span}`}>
                    <ArticleCard article={a} size={i === 0 ? "lg" : "md"} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
